import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import {
  endPortalVoiceCall,
  subscribePortalVoiceCallState,
  togglePortalVoiceMute,
} from 'src/mentor/components/voice/portalVoiceCallManager'

const LIVE_STATUSES = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending']

const STATUS_LABEL = {
  connecting: 'Preparing…',
  ringing: 'Ringing…',
  connected: 'Live',
  reconnecting: 'Reconnecting…',
  'on-hold': 'On hold',
  ending: 'Ending…',
  ended: 'Call ended',
}

function formatElapsed(seconds) {
  const safe = Math.max(0, Number(seconds) || 0)
  const minutes = Math.floor(safe / 60)
  const secs = safe % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

// Compact, always-on-top mini-player pinned bottom-right. The call keeps running here while the
// agent navigates the portal; click the body to restore the full surface.
export default function SupportCallPip({ contact, onRestore, onClose }) {
  const [callState, setCallState] = useState(null)

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])

  const status = callState?.status || 'idle'
  const isLive = LIVE_STATUSES.includes(status)
  const isEnded = status === 'ended'
  const subLabel = isLive
    ? `${STATUS_LABEL[status] || 'Live'}${callState?.connectedAtMs ? ` · ${formatElapsed(callState.elapsedSeconds)}` : ''}`
    : isEnded
      ? 'Call ended'
      : 'Tap to reopen'

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1240,
        width: 288,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.9)',
        boxShadow: '0 18px 44px rgba(15, 23, 42, 0.22)',
        animation: 'supportCallPipIn 180ms ease',
        '@keyframes supportCallPipIn': {
          from: { transform: 'translateY(12px)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 },
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.25,
          py: 1,
          cursor: 'pointer',
          background: isLive
            ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
            : 'linear-gradient(135deg, #475569 0%, #334155 100%)',
          color: '#fff',
        }}
        onClick={() => onRestore?.()}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.16)',
            flexShrink: 0,
          }}
        >
          <PhoneInTalkRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.2 }} noWrap>
            {contact?.name || 'Call'}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isLive && status !== 'connecting' ? (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#4ade80',
                  animation: 'supportPipPulse 1.4s ease-in-out infinite',
                  '@keyframes supportPipPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                }}
              />
            ) : null}
            <Typography sx={{ fontSize: 11, opacity: 0.9 }} noWrap>
              {subLabel}
            </Typography>
          </Stack>
        </Box>
        <Tooltip title="Reopen call">
          <OpenInFullRoundedIcon sx={{ fontSize: 16, opacity: 0.9 }} />
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={0.5} sx={{ px: 1, py: 0.75 }} justifyContent="space-between" alignItems="center">
        {isLive ? (
          <>
            <Tooltip title={callState?.muted ? 'Unmute' : 'Mute'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => togglePortalVoiceMute()}
                  disabled={callState?.holdActive}
                  sx={{
                    color: callState?.muted ? '#b45309' : '#475569',
                    backgroundColor: callState?.muted ? 'rgba(217, 119, 6, 0.12)' : 'transparent',
                  }}
                >
                  {callState?.muted ? <MicOffRoundedIcon sx={{ fontSize: 18 }} /> : <MicRoundedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="End call">
              <IconButton
                size="small"
                onClick={() => endPortalVoiceCall()}
                sx={{ color: '#fff', backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
              >
                <CallEndRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', pl: 0.5 }}>
              {isEnded ? 'Call ended' : 'Idle'}
            </Typography>
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => onClose?.()} sx={{ color: '#64748b' }}>
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    </Box>
  )
}
